import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isWeekend, isBeforeMarketOpen } from '../../utils/marketDate';
import { tickerDetailService } from '../../services/tickerDetailService';
import { useCompanyProfile, useSymbolHeader } from './contractMetrics';
import { isClosedPhase } from '../../services/symbolSummaryService';
import { fmtUsDate } from './format';
import { EXPLORER_CACHE } from './cachePolicy';

// Check if after market close (16:00 ET)
function isAfterMarketClose(): boolean {
  const now = new Date();
  const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const etHour = new Date(etString).getHours();
  return etHour >= 16;
}

function isMarketCurrentlyOpen(): boolean {
  return !isWeekend() && !isBeforeMarketOpen() && !isAfterMarketClose();
}

function fmt(n: number | null | undefined, decimals = 2): string {
  if (n == null || isNaN(n)) return '\u2014';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtCap(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toLocaleString()}`;
}

/**
 * Secondary stat pill.
 *
 * The price and day change carry the bar; everything else is context. Boxing
 * the context stops it competing with them and keeps the row legible however
 * many stats end up in it.
 */
function Badge({ label, children }: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-dark-border bg-dark-bg">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">{label}</span>
      {children}
    </span>
  );
}

interface HeroBarProps {
  symbol: string;
}

export function HeroBar({ symbol }: HeroBarProps): React.ReactElement {
  const [isMarketOpen, setIsMarketOpen] = useState(isMarketCurrentlyOpen());

  useEffect(() => {
    const interval = setInterval(() => {
      setIsMarketOpen(isMarketCurrentlyOpen());
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Price, change, 5-day, market cap and the 52-week band — one request,
  // shared with the Overview stat columns via the same query key.
  const header = useSymbolHeader(symbol);

  // Company name. `/v1/symbol-summary` deliberately omits it — the name is
  // static, so QuantForge serves it on the hourly `/v1/company-profile` plane
  // rather than re-sending it with every price refresh.
  const profile = useCompanyProfile(symbol);

  const change5d = header?.change5dPct ?? null;

  // Same query key as the Earnings section, so whichever loads first serves
  // the other from cache. Earnings dates are static — EXPLORER_CACHE is fine.
  const { data: earnings } = useQuery({
    queryKey: ['tickerEarnings', symbol],
    queryFn: () => tickerDetailService.getEarnings(symbol),
    ...EXPLORER_CACHE,
    enabled: !!symbol,
  });

  // The summary carries the next date directly. The earnings list stays as a
  // fallback for symbols the summary has no row for, and because the Earnings
  // section needs the full history anyway — this shares its cache.
  const today = new Date().toISOString().slice(0, 10);
  const nextEarningsDate =
    header?.nextEarnings ?? (earnings?.data ?? []).find((r) => r.date > today)?.date ?? null;

  const changePositive = (header?.change ?? 0) >= 0;

  // The server's phase knows about holidays; the client clock does not. Fall
  // back to the local guess only while the phase is still unknown.
  const closed = isClosedPhase(header?.sessionPhase) ?? !isMarketOpen;

  // Keyed on the summary alone: it carries every figure in the bar. The
  // company name arrives on its own slower request and simply appears when it
  // does — holding the whole bar back for a static string would be worse.
  if (!header) {
    return (
      <div className="sticky top-0 z-10 bg-chrome border-b border-dark-border px-4 py-3 flex items-center gap-4">
        <div className="animate-pulse flex gap-4">
          <div className="h-6 w-20 bg-dark-border rounded" />
          <div className="h-5 w-32 bg-dark-border rounded" />
          <div className="h-5 w-20 bg-dark-border rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-10 bg-chrome border-b border-dark-border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="text-accent-yellow font-numeric font-bold text-lg">{symbol}</span>
      {profile?.company_name && (
        <span className="text-white font-semibold text-sm">{profile.company_name}</span>
      )}
      {header && (
        <>
          <span className="font-numeric text-white font-bold text-xl leading-none">
            ${fmt(header.last)}
          </span>
          <span
            className={`font-numeric text-sm font-medium ${
              changePositive ? 'text-bull-blue' : 'text-bear-red'
            }`}
          >
            {changePositive ? '+' : ''}{fmt(header.change)} ({changePositive ? '+' : ''}
            {fmt(header.changePct)}%)
          </span>
          {change5d != null && (
            <Badge label="5D">
              <span
                className={`font-numeric font-medium ${
                  change5d >= 0 ? 'text-bull-blue' : 'text-bear-red'
                }`}
              >
                {change5d >= 0 ? '+' : ''}
                {fmt(change5d)}%
              </span>
            </Badge>
          )}
          <Badge label="Mkt Cap">
            <span className="font-numeric text-gray-200">{fmtCap(header.marketCap)}</span>
          </Badge>
          {closed && (
            <span className="text-xs text-gray-400 bg-dark-bg px-2 py-0.5 rounded">
              Market Closed
            </span>
          )}
        </>
      )}

      {nextEarningsDate && (
        <Badge label="Next Earnings">
          <span className="font-numeric text-gray-200">{fmtUsDate(nextEarningsDate)}</span>
        </Badge>
      )}
    </div>
  );
}
